import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import { Link, router, usePage } from "@inertiajs/react"
import { ListTodo, Plus, SquareStack } from "lucide-react"
import { useCallback, useState } from "react"

import { CreateListModal } from "@/components/CreateListModal"
import { ListUrgencyIndicator } from "@/components/ListUrgencyIndicator"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useDragReorder } from "@/hooks/use-drag-reorder"
import { listPath, listsPath, listsSortedListsPath } from "@/routes"
import type { List } from "@/types"

export function ListsSidebar() {
  const {
    lists = [],
    currentListId,
    isDashboard,
  } = usePage().props as {
    lists?: List[]
    currentListId?: number
    isDashboard?: boolean
  }

  const { state: sidebarState } = useSidebar()
  const isCollapsed = sidebarState === "collapsed"

  const [showNewListModal, setShowNewListModal] = useState(false)

  const handleReordered = useCallback((newLists: List[]) => {
    router.post(
      listsSortedListsPath(),
      { list_ids: newLists.map((list) => list.id) },
      { preserveScroll: true, preserveState: true, only: [] },
    )
  }, [])

  const { orderedItems: orderedLists, handleDragEnd } = useDragReorder<List>({
    items: lists,
    onReordered: handleReordered,
  })

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isDashboard}>
                <Link href={listsPath()} prefetch>
                  <SquareStack className="h-4 w-4" />
                  <span>All Tasks</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ListTodo className="h-4 w-4" />
            Lists
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto p-0"
                onClick={() => setShowNewListModal(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Create new list</TooltipContent>
          </Tooltip>
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {orderedLists.length === 0 ? (
              <SidebarMenuItem>
                <SidebarMenuButton disabled>No lists yet</SidebarMenuButton>
              </SidebarMenuItem>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="lists-sidebar">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {orderedLists.map((list, index) => (
                        <Draggable
                          key={list.id}
                          draggableId={`list-${list.id}`}
                          index={index}
                        >
                          {(dragProvided, snapshot) => (
                            <SidebarMenuItem
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={
                                snapshot.isDragging ? "opacity-80" : ""
                              }
                            >
                              <SidebarMenuButton
                                asChild
                                isActive={list.id === currentListId}
                                className="group/list-item"
                              >
                                <Link
                                  href={listPath(list.id)}
                                  prefetch
                                  className="flex w-full items-center"
                                >
                                  {!isCollapsed && (
                                    <ListUrgencyIndicator
                                      {...dragProvided.dragHandleProps}
                                      criticalCount={list.critical_count ?? 0}
                                      warningCount={list.warning_count ?? 0}
                                      className="mr-1"
                                    />
                                  )}
                                  <span className="min-w-0 flex-1 truncate">
                                    {list.name}
                                  </span>
                                  {!isCollapsed &&
                                    (list.active_task_count ?? 0) > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="ml-auto h-5 min-w-5 justify-center px-1.5 text-xs"
                                      >
                                        {list.active_task_count}
                                      </Badge>
                                    )}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <CreateListModal
        open={showNewListModal}
        onOpenChange={setShowNewListModal}
      />
    </>
  )
}
